import { responseErrorCodes } from '@team-milva/lab-constants'
import * as Sentry from '@sentry/vue'
import { ENV } from './host'

/**
 * Errors already reported, so a failure travelling up through several catch
 * blocks produces one Sentry event rather than one per layer. A WeakSet rather
 * than a property on the error, since Firebase's own error objects should not
 * be mutated and some are frozen.
 */
const reported = new WeakSet<object>()

export interface IReportErrorContext {
  /**
   * What was being attempted, kebab-case. Becomes Sentry's `operation` tag and
   * is what you filter on — e.g. 'file-upload', 'insurance-customer-account-save'.
   */
  operation: string
  /**
   * Detail attached to the event: storage paths, record IDs, sizes, error
   * codes. Never user content or anything personal — this is stored in Sentry.
   */
  extra?: Record<string, unknown>
}

/**
 * Reports a failure that lab-cloud can never see, because it never reached
 * lab-cloud: a Firebase Storage or Firestore call made straight from the
 * browser, a request that never arrived, or a bug in a view or store.
 *
 * Without this, such a failure is reported nowhere at all — `captureConsoleIntegration`
 * only picks up `console.error`, and Sentry's automatic capture only sees
 * *unhandled* errors, so a caught one is invisible by construction.
 *
 * Do **not** use this for a failed lab-cloud API call: anything that reached
 * lab-cloud was already reported there by `globalErrorHandler`, with a real
 * server stack trace and the request body. Reporting it again only adds a
 * minified duplicate. Use `reportUnexpectedError` when a catch block covers
 * both kinds.
 *
 * Calling it twice with the same error object reports once. Sentry is not
 * initialised on localhost (see `main.ts`), so there it logs to the console
 * instead.
 */
export function reportError(
  error: unknown,
  { operation, extra }: IReportErrorContext
): void {
  if (typeof error === 'object' && error !== null) {
    if (reported.has(error)) return
    reported.add(error)
  }

  if (ENV.isLocalhost) {
    // Sentry is not initialised on localhost, so the console is the only place
    // this can go — and a swallowed error with no trace is the whole problem
    // this function exists to fix.
    // eslint-disable-next-line no-console
    console.error(`[${operation}]`, error, extra ?? '')
    return
  }

  Sentry.withScope((scope) => {
    scope.setTag('operation', operation)
    // Separates a deliberate client-side report from the unhandled errors and
    // console.error calls Sentry picks up on its own.
    scope.setTag('reported', 'client')
    scope.setContext('operation-detail', {
      url: window.location.href,
      ...extra,
    })
    Sentry.captureException(error)
  })
}

/**
 * True when the error is a lab-cloud API failure surfaced by a store, which
 * throws `new Error(result.code)` when `result.success` is false — so the
 * message is one of the shared response codes.
 *
 * lab-cloud has already reported anything that failed on its side, so these
 * must not be reported again from here. The check lives in one place because
 * it leans on that store convention; if a store ever throws a richer error
 * type, teach this function about it rather than duplicating the test.
 */
export function isApiFailure(error: unknown): boolean {
  return (
    error instanceof Error &&
    (responseErrorCodes as readonly string[]).includes(error.message)
  )
}

/**
 * Reports an error only if nothing else already has: skips lab-cloud API
 * failures (reported server-side) and errors a lower layer already reported.
 *
 * Use this in a catch block that wraps several kinds of call — a form submit
 * that uploads a file and then saves it, say — where the interesting case is
 * the unexpected one that nothing else would record.
 */
export function reportUnexpectedError(
  error: unknown,
  context: IReportErrorContext
): void {
  if (isApiFailure(error)) return
  reportError(error, context)
}

/**
 * Reports an apiClient transport failure: the request never arrived
 * (`network-error`), or the response was not a recognisable envelope
 * (`invalid-response`).
 *
 * These are the only two API failures lab-cloud cannot report itself — nothing
 * reached `globalErrorHandler`, so no server-side Sentry event or Slack alert
 * exists. Every other `success: false` was already reported server-side and
 * must not be reported again from here.
 */
export function reportApiTransportFailure(
  api: string,
  info: {
    reason: string
    method: string
    url: string
    status?: number
    error?: unknown
  }
): void {
  const { reason, method, url, status, error } = info

  // `invalid-response` has no thrown error to attach, so synthesise one —
  // Sentry needs an Error to produce anything useful.
  reportError(error ?? new Error(`${api} api: ${reason}`), {
    operation: `api-${reason}`,
    extra: { api, method, url, status: status ?? null },
  })
}
